import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchArgs } from '@reduxjs/toolkit/query';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import {
  ShippingApi,
  ShippingListApi,
  ShippingsFilter,
  ShippingEnrichmentResult,
  RunShippingEnrichmentRequest,
  ShippingPackageApi,
  ShippingPackageCreateDto,
  ShippingPackageUpdateDto,
} from '@/pages/Business/pages/Shippings/models';
import { ShippingAtcInfo, ShippingBasicInfo } from '@/pages/Business/pages/Shippings/models/Shipping';
import { API_BUSINESS_URL } from '@/utils';
import { fetchMeliIntegration } from '@/utils/meliIntegration';
import {
  hasMeliOrderIdentifier,
  type MeliCommerceOption,
  type MeliOrdersQuery,
  type MeliOrdersResponse,
  type SellerApi,
} from '../pages/MeliOrders/models/MeliOrder';
import type {
  ExportJob,
  InitiateExportResponse,
} from '@/pages/Business/pages/Monitoring/models/Export';
import type {
  ImportUploadResponse,
  ImportJobStatusResponse,
} from '@/pages/BusinessCommerces/pages/Shippings/models/ImportShipping';
import type {
  CorreoDeliveryImportUploadResponse,
  CorreoDeliveryImportJobStatusResponse,
} from '../models/CorreoDeliveryImport';

const LABEL_API_URL = import.meta.env.VITE_LABEL_API_URL || '/label-proxy';

export type LabelBase64Response = {
  pdfBase64: string | string[];
  fileName: string | string[];
  shippingNumber: number;
  labelProvider: string;
};

/**
 * Proveedores de etiqueta. Solo un admin puede forzar uno desde el detalle del
 * pedido; el resto de los usuarios siempre recibe el que resuelven las reglas.
 */
export enum LabelProvider {
  /** La etiqueta del correo. */
  AHIVA = 'AHIVA',
  /** La etiqueta propia. */
  SOYDELIVERY = 'SOYDELIVERY',
}

export const LABEL_PROVIDER_LABELS: Record<LabelProvider, string> = {
  [LabelProvider.AHIVA]: 'Etiqueta del correo',
  [LabelProvider.SOYDELIVERY]: 'Etiqueta SoyDelivery',
};

export type CombinedLabelsJobInitResponse = {
  jobId: string;
  fileName: string;
  s3Key: string;
};

export enum CombinedLabelsJobStatus {
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED',
  ERRORED = 'ERRORED',
}

export type RouteLabelsJobInitResponse = CombinedLabelsJobInitResponse & {
  shippingCount: number;
};

export type RouteLabelsJobInitRequest = {
  routeId: string;
  logisticOperatorId: string;
};

export type CombinedLabelsFailedShipping = {
  shippingNumber: number;
  errorMessage: string;
};

export type CombinedLabelsJob = {
  status: CombinedLabelsJobStatus;
  downloadUrl?: string;
  errorMessage?: string;
  rowCount?: number;
  fileName?: string;
  filterPayload?: {
    result?: {
      shippingCount?: number;
      pageCount?: number;
      failedShippings?: CombinedLabelsFailedShipping[];
    };
  } | null;
};

const trimFilterValue = (value?: string) => value?.trim() || undefined;

/**
 * `dateToDelivery` (ETA) es una columna DATEONLY (día calendario, sin hora). El
 * backend compara el valor recibido formateándolo con la zona horaria del server
 * (Sequelize `moment(date).format('YYYY-MM-DD')`). Si mandamos el borde en UTC
 * (00:00Z), un server en UTC-3 lo interpreta como las 21:00 del día anterior y el
 * filtro "de hoy a hoy" termina trayendo el día previo (y con el ETA guardado
 * corrido, también el siguiente).
 *
 * Para que el día calendario elegido no se corra ni al anterior ni al posterior
 * —sea cual sea el offset del server (UTC-3, UTC-5, UTC…)— anclamos ese día al
 * mediodía UTC. Como el ETA se compara a nivel de día, el rango [día, día] filtra
 * exactamente ese día.
 */
const toDeliveryDayParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T12:00:00.000Z`;
};

/**
 * Con un identificador cargado no se mandan fechas: el pedido se busca en todo el
 * historial del micro y no sólo en el rango que haya quedado en pantalla.
 */
export const buildMeliOrdersSearchParams = ({
  filter,
  sort,
  page,
  pageSize,
}: MeliOrdersQuery): URLSearchParams => {
  const params = new URLSearchParams();
  const set = (key: string, value?: string) => {
    const clean = trimFilterValue(value);
    if (clean) params.set(key, clean);
  };

  set('orderId', filter.orderId);
  set('externalId', filter.externalId);
  set('businessShippingNumber', filter.businessShippingNumber);
  set('buyerName', filter.buyerName);
  set('commerceCode', filter.commerceCode);
  set('driverName', filter.driverName);
  set('sdStatus', filter.sdStatus);
  set('meliStatus', filter.meliStatus);
  if (!hasMeliOrderIdentifier(filter)) {
    set('dateFrom', filter.dateFrom);
    set('dateTo', filter.dateTo);
    set('deliveryDateFrom', filter.deliveryDateFrom);
    set('deliveryDateTo', filter.deliveryDateTo);
  }
  params.set('sortBy', sort.field);
  params.set('sortDir', sort.direction);
  params.set('limit', String(pageSize));
  params.set('offset', String((page - 1) * pageSize));
  return params;
};

export type GetShippingsQueryArgs = {
  filters: ShippingsFilter;
  paginated: PaginatedRequest;
};

export type LastDeliveryAddressApi = {
  id: string;
  streetName: string;
  doorNumber: string;
  apartmentNumber: string | null;
  corner: string | null;
  zipCode: string | null;
  lat: string | null;
  long: string | null;
  neighborhoodId: string | null;
};

/**
 * Respuesta del endpoint que devuelve el último pedido creado para un teléfono
 * o un ID de destinatario dentro de un comercio. Un 404 significa "sin pedidos
 * previos" y es esperado.
 */
export type LastDeliveryAddressResponse = {
  shippingNumber: number;
  recipient: string;
  recipientPhoneNumber: string | null;
  recipientId: string | null;
  address: LastDeliveryAddressApi;
};

export type LastDeliveryLookupKind = 'phone' | 'recipientId';

export type GetLastDeliveryAddressArgs = {
  kind: LastDeliveryLookupKind;
  value: string;
  commerceId: string;
};

// Los endpoints de /labels viven bajo la raíz de API_BUSINESS_URL, no bajo
// /shipping (el baseQuery de este servicio). Usamos un baseQuery propio, con el
// mismo mecanismo de reauth (refresh + retry ante 401/"Token inválido"), en vez
// de un fetch manual que dejaba al usuario colgado cuando el access token vencía.
const labelsBaseQuery = createBaseQueryWithReauth(API_BUSINESS_URL);

const fetchLabelsEndpoint = async (
  args: string | FetchArgs,
  api: Parameters<ReturnType<typeof createBaseQueryWithReauth>>[1],
  extraOptions: Parameters<ReturnType<typeof createBaseQueryWithReauth>>[2]
) => {
  const result = await labelsBaseQuery(args, api, extraOptions);
  if (result.error) {
    return {
      error: {
        status: result.error.status,
        data: {
          message:
            (result.error.data as { error?: string; message?: string })
              ?.error ||
            (result.error.data as { error?: string; message?: string })
              ?.message ||
            'Ocurrió un error al comunicarse con el servicio de etiquetas',
        },
      },
    };
  }
  return { data: result.data };
};

/**
 * Traduce los filtros de la grilla a query params. Lo comparten
 * `getShippings` (respuesta completa) y `getShippingsList` (recortada), asi
 * que los dos filtran exactamente igual.
 */
const buildShippingsListQuery = (
  { paginated, filters }: GetShippingsQueryArgs,
  url: string
) => {
  const {
    commerceId,
    logisticOperatorId,
    driverId,
    number,
    commerceShippingNumber,
    labelNumber,
    recipient,
    recipientPhoneNumber,
    destinationAddress,
    driverName,
    driverPhoneNumber,
    originAddress,
    cityIds,
    destinationStateOrDepartmentIds,
    originStateOrDepartmentIds,
    timeSlotIds,
    branchId,
    branch,
    tags,
    serviceWithOperatorIds,
    statusId,
    shippingLevels,
    deliveryDateFrom,
    deliveryDateTo,
    dateDeliveredFrom,
    dateDeliveredTo,
    createdAtFrom,
    createdAtTo,
    zoneIds,
  } = filters;

  const numberTrimmed = number?.trim() ?? '';
  if (numberTrimmed) {
    return {
  url,
  params: {
    limit: paginated.pageSize,
    offset: (paginated.page - 1) * paginated.pageSize,
    number: numberTrimmed,
  },
    };
  }

  const commerceShippingNumberTrimmed =
    commerceShippingNumber?.trim() ?? '';
  if (commerceShippingNumberTrimmed) {
    return {
  url,
  params: {
    limit: paginated.pageSize,
    offset: (paginated.page - 1) * paginated.pageSize,
    commerceShippingNumber: commerceShippingNumberTrimmed,
  },
    };
  }

  const labelNumberTrimmed = labelNumber?.trim() ?? '';
  if (labelNumberTrimmed) {
    return {
  url,
  params: {
    limit: paginated.pageSize,
    offset: (paginated.page - 1) * paginated.pageSize,
    labelNumber: labelNumberTrimmed,
  },
    };
  }

  const params: Record<string, string | number | boolean> = {
    limit: paginated.pageSize,
    offset: (paginated.page - 1) * paginated.pageSize,
    include:
  'commerce,status,channel,originAddress,destinationAddress,commercialService,serviceWithOperator.commercialService,shippingZones',
    ...(commerceId && { commerceId }),
    ...(driverId && { driverId }),
    ...(logisticOperatorId &&
  typeof logisticOperatorId === 'string' &&
  logisticOperatorId.trim() !== '' && {
    logisticOperatorId: logisticOperatorId.trim(),
  }),
    ...(recipient &&
  recipient.trim() !== '' && { recipient: recipient.trim() }),
    ...(recipientPhoneNumber &&
  recipientPhoneNumber.trim() !== '' && {
    recipientPhoneNumber: recipientPhoneNumber.trim(),
  }),
    ...(destinationAddress &&
  destinationAddress.trim() !== '' && {
    destinationAddress: destinationAddress.trim(),
  }),
    ...(driverName &&
  driverName.trim() !== '' && { driverName: driverName.trim() }),
    ...(driverPhoneNumber &&
  driverPhoneNumber.trim() !== '' && {
    driverPhoneNumber: driverPhoneNumber.trim(),
  }),
    ...(originAddress &&
  originAddress.trim() !== '' && {
    originAddress: originAddress.trim(),
  }),
    ...(cityIds && cityIds.length > 0 && { cityIds: cityIds.join(',') }),
    ...(destinationStateOrDepartmentIds &&
  destinationStateOrDepartmentIds.length > 0 && {
    destinationStateOrDepartmentIds:
      destinationStateOrDepartmentIds.join(','),
  }),
    ...(originStateOrDepartmentIds &&
  originStateOrDepartmentIds.length > 0 && {
    originStateOrDepartmentIds: originStateOrDepartmentIds.join(','),
  }),
    ...(timeSlotIds &&
  timeSlotIds.length > 0 && { timeSlotIds: timeSlotIds.join(',') }),
    ...(branchId && { branchId }),
    ...(branch && branch.trim() !== '' && { branch: branch.trim() }),
    ...(tags &&
  tags.trim() !== '' && {
    tags: tags.trim(),
  }),
    ...(serviceWithOperatorIds &&
  serviceWithOperatorIds.length > 0 && {
    serviceWithOperatorIds: serviceWithOperatorIds.join(','),
  }),
    ...(zoneIds &&
  zoneIds.length > 0 && {
    zoneIds: zoneIds.join(','),
  }),
    ...(statusId && { statusId }),
    ...(shippingLevels &&
  shippingLevels.length > 0 && {
    shippingLevels: shippingLevels.join(','),
  }),
    ...(zoneIds && zoneIds.length > 0
  ? {}
  : {
      ...(deliveryDateFrom && {
        deliveryDateFrom: toDeliveryDayParam(deliveryDateFrom),
      }),
      ...(deliveryDateTo && {
        deliveryDateTo: toDeliveryDayParam(deliveryDateTo),
      }),
    }),
    ...(dateDeliveredFrom && {
  dateDeliveredFrom: dateDeliveredFrom.toISOString(),
    }),
    ...(dateDeliveredTo && {
  dateDeliveredTo: dateDeliveredTo.toISOString(),
    }),
    ...(!(zoneIds && zoneIds.length > 0) &&
  createdAtFrom && { dateFrom: createdAtFrom.toISOString() }),
    ...(!(zoneIds && zoneIds.length > 0) &&
  createdAtTo && { dateTo: createdAtTo.toISOString() }),
  };

  return {
    url,
    params,
  };
};

const shippingsService = createApi({
  reducerPath: 'shippingsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/shipping`),
  tagTypes: ['Shippings', 'ShippingPackages'],
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: 60,
  endpoints: (builder) => ({
    getShippings: builder.query<
      PaginatedResponse<ShippingApi>,
      GetShippingsQueryArgs
    >({
      query: (args: GetShippingsQueryArgs) => buildShippingsListQuery(args, ''),
      providesTags: ['Shippings'],
    }),

    /**
     * Listado de la grilla: mismos filtros, respuesta recortada a las columnas
     * visibles. `GET /shipping` queda intacto para el resto de las pantallas.
     */
    getShippingsList: builder.query<
      PaginatedResponse<ShippingListApi>,
      GetShippingsQueryArgs
    >({
      query: (args: GetShippingsQueryArgs) =>
        buildShippingsListQuery(args, '/list'),
      providesTags: ['Shippings'],
    }),

    getShippingBasicInfoByCode: builder.query<
      ShippingBasicInfo,
      { code: string; mode: 'NUMBER' | 'COMMERCIAL_NUMBER' }
    >({
      query: ({ code, mode }) => ({
        url: `/find-by-code/${encodeURIComponent(code)}`,
        params: { mode },
      }),
      providesTags: ['Shippings'],
    }),

    getShippingAtcInfo: builder.query<ShippingAtcInfo, { number: string }>({
      query: ({ number }) => ({
        url: `/${number}/atc-info`,
      }),
      providesTags: ['Shippings'],
    }),

    getShippingById: builder.query<ShippingApi, { number: string }>({
      query: ({ number }) => ({
        url: `/${number}`,
        params: {
          include:
            'shippingZones,shippingProducts.productSize,commerce,logisticOperator,subOperadorLogistico,branch,commercialService,serviceWithOperator.commercialService,status,size,classification,complexity,type,channel,timeSlot,originAddress,destinationAddress,originCountry,originStateOrDepartment,originCity,originNeighborhood,destinationCountry,destinationStateOrDepartment,destinationCity,destinationNeighborhood',
        },
      }),
      providesTags: ['Shippings'],
    }),

    getShippingByNumber: builder.query<ShippingApi, { number: string }>({
      query: ({ number }) => ({
        url: `/${number}`,
        params: {
          include:
            'shippingZones,shippingProducts.productSize,commerce,logisticOperator,subOperadorLogistico,branch,commercialService,serviceWithOperator.commercialService,status,size,classification,complexity,type,channel,timeSlot,originAddress,destinationAddress,originCountry,originStateOrDepartment,originCity,originNeighborhood,destinationCountry,destinationStateOrDepartment,destinationCity,destinationNeighborhood',
        },
      }),
      providesTags: ['Shippings'],
    }),

    getLastDeliveryAddress: builder.query<
      LastDeliveryAddressResponse,
      GetLastDeliveryAddressArgs
    >({
      query: ({ kind, value, commerceId }) => ({
        url:
          kind === 'recipientId'
            ? `/last-delivery-address/by-recipient-id/${encodeURIComponent(value)}`
            : `/last-delivery-address/${encodeURIComponent(value)}`,
        params: { commerceId },
      }),
    }),

    createShipping: builder.mutation<ShippingApi, Partial<ShippingApi>>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Shippings'],
    }),

    updateShipping: builder.mutation<
      ShippingApi,
      { number: string; body: Partial<ShippingApi> }
    >({
      query: ({ number, body }) => ({
        url: `/${number}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Shippings'],
    }),

    deleteShipping: builder.mutation<void, { number: string }>({
      query: ({ number }) => ({
        url: `/${number}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Shippings'],
    }),

    runShippingEnrichment: builder.mutation<
      ShippingEnrichmentResult,
      RunShippingEnrichmentRequest
    >({
      query: ({ number, steps, forceRegenerateLabel }) => ({
        url: `/${number}/enrichment`,
        method: 'POST',
        body: {
          ...(steps ? { steps } : {}),
          ...(forceRegenerateLabel != null ? { forceRegenerateLabel } : {}),
        },
      }),
      invalidatesTags: ['Shippings'],
    }),

    removeFromRoute: builder.mutation<void, { number: string }>({
      query: ({ number }) => ({
        url: `/${number}/remove-from-route`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Shippings'],
    }),

    assignShipping: builder.mutation<
      void,
      { number: string; driverId: string; routeLogisticOperatorId?: string }
    >({
      query: ({ number, driverId, routeLogisticOperatorId }) => ({
        url: `/${number}/assign-shipping`,
        method: 'POST',
        body: routeLogisticOperatorId
          ? { driverId, routeLogisticOperatorId }
          : { driverId },
      }),
      invalidatesTags: ['Shippings'],
    }),

    updateShippingSize: builder.mutation<
      void,
      { shippingNumber: number; sizeId: string }
    >({
      query: (body) => ({
        url: '/update-size',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Shippings'],
    }),

    getShippingPackages: builder.query<
      ShippingPackageApi[],
      { number: string }
    >({
      query: ({ number }) => ({
        url: `/${number}/packages`,
      }),
      providesTags: (_result, _error, { number }) => [
        { type: 'ShippingPackages', id: number },
      ],
    }),

    generateShippingPackages: builder.mutation<
      ShippingPackageApi[],
      { number: string; quantity: number }
    >({
      query: ({ number, quantity }) => ({
        url: `/${number}/packages/generate`,
        method: 'POST',
        body: { quantity },
      }),
      invalidatesTags: (_result, _error, { number }) => [
        { type: 'ShippingPackages', id: number },
      ],
    }),

    createShippingPackage: builder.mutation<
      ShippingPackageApi,
      { number: string; body: ShippingPackageCreateDto }
    >({
      query: ({ number, body }) => ({
        url: `/${number}/packages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { number }) => [
        { type: 'ShippingPackages', id: number },
      ],
    }),

    updateShippingPackage: builder.mutation<
      ShippingPackageApi,
      { number: string; packageId: string; body: ShippingPackageUpdateDto }
    >({
      query: ({ number, packageId, body }) => ({
        url: `/${number}/packages/${packageId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { number }) => [
        { type: 'ShippingPackages', id: number },
      ],
    }),

    deleteShippingPackage: builder.mutation<
      void,
      { number: string; packageId: string }
    >({
      query: ({ number, packageId }) => ({
        url: `/${number}/packages/${packageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { number }) => [
        { type: 'ShippingPackages', id: number },
      ],
    }),

    updateShippingStatus: builder.mutation<
      ShippingApi,
      {
        number: string;
        status: { externalId: string; name: string };
        user: string;
      }
    >({
      query: ({ number, status, user }) => ({
        url: '/event/event-update-status',
        method: 'POST',
        body: {
          eventName: 'event_update_status',
          eventData: {
            number,
            status,
            date: new Date().toISOString(),
          },
          user,
        },
      }),
      invalidatesTags: ['Shippings'],
    }),

    printShippingLabel: builder.mutation<
      Blob,
      { negocioId: string; negocioClave: string; pedidoId: string }
    >({
      queryFn: async ({ negocioId, negocioClave, pedidoId }) => {
        try {
          const response = await fetch(`${LABEL_API_URL}/aWSEtiquetaPedido1`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              Negocio_id: negocioId,
              Negocio_clave: negocioClave,
              Pedido_id: parseInt(pedidoId, 10),
            }),
          });

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: { message: 'Error al obtener la etiqueta' },
              },
            };
          }

          const contentType = response.headers.get('content-type');

          if (contentType?.includes('application/json')) {
            const jsonData = await response.json();

            if (jsonData.Etiqueta_base64) {
              const base64Data = jsonData.Etiqueta_base64;
              const binaryString = window.atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: 'application/pdf' });
              return { data: blob };
            }

            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: `Respuesta JSON inesperada: ${JSON.stringify(jsonData)}`,
              },
            };
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error:
                error instanceof Error ? error.message : 'Error de conexión',
            },
          };
        }
      },
    }),

    getShippingLabelBase64: builder.query<
      LabelBase64Response,
      | number
      | {
          shippingNumber: number;
          forceRegenerateLabel?: boolean;
          /** Solo admin: saltea la evaluación de reglas y fuerza el proveedor. */
          labelProvider?: LabelProvider;
        }
    >({
      queryFn: (arg, api, extraOptions) => {
        const shippingNumber = typeof arg === 'number' ? arg : arg.shippingNumber;
        const params = new URLSearchParams();
        if (typeof arg !== 'number') {
          if (arg.forceRegenerateLabel) params.set('forceRegenerateLabel', 'true');
          if (arg.labelProvider) params.set('labelProvider', arg.labelProvider);
        }
        const queryString = params.toString();
        const url = `labels/${shippingNumber}/pdf-base64${
          queryString ? `?${queryString}` : ''
        }`;
        return fetchLabelsEndpoint({ url }, api, extraOptions) as Promise<
          { data: LabelBase64Response } | { error: any }
        >;
      },
    }),

    initiateCombinedLabelsPdf: builder.mutation<
      CombinedLabelsJobInitResponse,
      number[]
    >({
      queryFn: (shippingNumbers, api, extraOptions) =>
        fetchLabelsEndpoint(
          {
            url: 'labels/pdf-combined',
            method: 'POST',
            body: { shippingNumbers },
          },
          api,
          extraOptions
        ) as Promise<{ data: CombinedLabelsJobInitResponse } | { error: any }>,
    }),

    initiateRouteLabelsPdf: builder.mutation<
      RouteLabelsJobInitResponse,
      RouteLabelsJobInitRequest
    >({
      queryFn: ({ routeId, logisticOperatorId }, api, extraOptions) =>
        fetchLabelsEndpoint(
          {
            url: `labels/routes/${routeId}/pdf-combined`,
            method: 'POST',
            headers: { 'x-logistic-operator-id': logisticOperatorId },
          },
          api,
          extraOptions
        ) as Promise<{ data: RouteLabelsJobInitResponse } | { error: any }>,
    }),

    getCombinedLabelsPdfJobStatus: builder.query<CombinedLabelsJob, string>({
      queryFn: (jobId, api, extraOptions) =>
        fetchLabelsEndpoint(
          { url: `labels/pdf-combined/jobs/${jobId}` },
          api,
          extraOptions
        ) as Promise<{ data: CombinedLabelsJob } | { error: any }>,
    }),

    searchMeliOrders: builder.query<MeliOrdersResponse, MeliOrdersQuery>({
      queryFn: (query) =>
        fetchMeliIntegration<MeliOrdersResponse>(
          `/orders/search?${buildMeliOrdersSearchParams(query).toString()}`
        ),
    }),

    getMeliOrderCommerces: builder.query<MeliCommerceOption[], void>({
      queryFn: () =>
        fetchMeliIntegration<MeliCommerceOption[]>('/orders/search/commerces'),
    }),

    countShippingExport: builder.mutation<
      {
        count: number;
        limit: number;
        /** Presentes solo cuando `count` es 0, para explicar el motivo. */
        emptyReason?: 'OUT_OF_DATE_RANGE' | 'NO_MATCHES';
        countIgnoringDates?: number;
      },
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/export-excel/count',
        method: 'POST',
        body,
      }),
    }),
    initiateShippingExport: builder.mutation<
      InitiateExportResponse,
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/export-excel',
        method: 'POST',
        body,
      }),
    }),
    getShippingExportJobStatus: builder.query<ExportJob, string>({
      query: (jobId) => ({
        url: `/export-excel/jobs/${jobId}`,
      }),
    }),

    uploadShippingImportExcel: builder.mutation<ImportUploadResponse, FormData>({
      query: (formData) => ({
        url: '/import-excel',
        method: 'POST',
        body: formData,
      }),
    }),
    getShippingImportJobStatus: builder.query<ImportJobStatusResponse, string>({
      query: (jobId) => ({
        url: `/import-excel/jobs/${jobId}`,
      }),
    }),

    uploadCorreoDeliveryImport: builder.mutation<
      CorreoDeliveryImportUploadResponse,
      FormData
    >({
      query: (formData) => ({
        url: '/correo-deliveries/import',
        method: 'POST',
        body: formData,
      }),
    }),
    getCorreoDeliveryImportJobStatus: builder.query<
      CorreoDeliveryImportJobStatusResponse,
      string
    >({
      query: (jobId) => ({
        url: `/correo-deliveries/import/jobs/${jobId}`,
      }),
    }),

    getMeliSellers: builder.query<SellerApi[], void>({
      queryFn: () => fetchMeliIntegration<SellerApi[]>('/sellers'),
    }),
  }),
});

export default shippingsService;

export const {
  useGetShippingsQuery,
  useGetShippingsListQuery,
  useLazyGetShippingsQuery,
  useGetShippingAtcInfoQuery,
  useLazyGetShippingAtcInfoQuery,
  useGetShippingBasicInfoByCodeQuery,
  useLazyGetShippingBasicInfoByCodeQuery,
  useGetShippingByIdQuery,
  useGetShippingByNumberQuery,
  useLazyGetShippingByNumberQuery,
  useGetLastDeliveryAddressQuery,
  useLazyGetLastDeliveryAddressQuery,
  useCreateShippingMutation,
  useUpdateShippingMutation,
  useDeleteShippingMutation,
  useRunShippingEnrichmentMutation,
  useUpdateShippingStatusMutation,
  usePrintShippingLabelMutation,
  useRemoveFromRouteMutation,
  useAssignShippingMutation,
  useUpdateShippingSizeMutation,
  useGetShippingPackagesQuery,
  useGenerateShippingPackagesMutation,
  useCreateShippingPackageMutation,
  useUpdateShippingPackageMutation,
  useDeleteShippingPackageMutation,
  useSearchMeliOrdersQuery,
  useLazySearchMeliOrdersQuery,
  useGetMeliOrderCommercesQuery,
  useGetMeliSellersQuery,
  useCountShippingExportMutation,
  useInitiateShippingExportMutation,
  useLazyGetShippingExportJobStatusQuery,
  useUploadShippingImportExcelMutation,
  useGetShippingImportJobStatusQuery,
  useUploadCorreoDeliveryImportMutation,
  useGetCorreoDeliveryImportJobStatusQuery,
  useLazyGetShippingLabelBase64Query,
  useInitiateCombinedLabelsPdfMutation,
  useInitiateRouteLabelsPdfMutation,
  useLazyGetCombinedLabelsPdfJobStatusQuery,
} = shippingsService;
